import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { fetchArea, fetchMonthPipeline, fetchSourcePerformance, fetchParameters, fetchMonthlyProjection, fetchRevenue6Month } from '@/lib/api';
import { formatINR, formatDate } from '@/lib/format';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, Line, ComposedChart, CartesianGrid, ReferenceLine } from 'recharts';
import { CalendarRange, ArrowUpRight, TrendingUp, Printer } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const LOGO_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_sales-analytics-base/artifacts/bju11je1_Final%20Logo-01.webp";

export default function Analytics() {
    const [area, setArea] = useState([]);
    const [sources, setSources] = useState([]);
    const [monthPipe, setMonthPipe] = useState([]);
    const [params, setParams] = useState({});
    const [areaFilter, setAreaFilter] = useState({ win_loss: '', sales_owner: '', service_line: '' });
    const [selMonth, setSelMonth] = useState('');
    const [projection, setProjection] = useState(null);
    const [rev6, setRev6] = useState(null);
    const [drilldown, setDrilldown] = useState(null);
    const nav = useNavigate();

    useEffect(() => {
        fetchParameters().then(setParams);
        fetchSourcePerformance().then(setSources);
        fetchMonthlyProjection().then(setProjection);
        fetchRevenue6Month().then(setRev6);
    }, []);
    useEffect(() => { fetchArea(areaFilter).then(setArea); }, [areaFilter]);
    useEffect(() => { fetchMonthPipeline(selMonth).then(setMonthPipe); }, [selMonth]);

    const printProjection = () => {
        document.body.classList.add('printing-analytics');
        const cleanup = () => {
            document.body.classList.remove('printing-analytics');
            window.removeEventListener('afterprint', cleanup);
        };
        window.addEventListener('afterprint', cleanup);
        // small timeout ensures the class is applied before the print dialog opens
        setTimeout(() => window.print(), 50);
    };

    const printedOn = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

    const COLORS = ['#016a6d', '#189290', '#34C759', '#FF9F0A', '#888', '#a35a2a'];

    return (
        <div>
            <PageHeader
                title="Analytics"
                subtitle="Forecast · Projection · Sources"
                actions={
                    <Button
                        onClick={printProjection}
                        data-testid="btn-print-projection"
                        variant="outline"
                        className="border-[#E2E8F0] bg-white hover:bg-slate-50 gap-2"
                    >
                        <Printer className="w-4 h-4" /> Print Projection
                    </Button>
                }
            />
            <div className="p-4 md:p-6 space-y-6">
                {/* Revenue 6-month (past 3 + current 3) */}
                {rev6 && (
                    <div className="surface-1 rounded-md p-4 md:p-6 no-print" data-testid="revenue-6month-widget">
                        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <TrendingUp className="w-4 h-4 text-[#016a6d]" />
                                    <div className="overline">Revenue Forecast · 6-Month Strip</div>
                                </div>
                                <h3 className="font-display text-lg md:text-xl font-bold text-slate-900 mt-1">Previous 3 · Current · Next 2</h3>
                                <p className="text-[11px] text-slate-500 mt-1">Past = actual Won revenue by expected month. Current + future = weighted pipeline projection.</p>
                            </div>
                            <div className="flex gap-4 text-right">
                                <div>
                                    <div className="overline text-[#34C759]">Past Won</div>
                                    <div className="font-display text-lg md:text-xl font-bold text-[#34C759] tabular-nums mt-1">{formatINR(rev6.total_actual)}</div>
                                </div>
                                <div>
                                    <div className="overline text-[#016a6d]">Projected</div>
                                    <div className="font-display text-lg md:text-xl font-bold text-[#016a6d] tabular-nums mt-1">{formatINR(rev6.total_projected)}</div>
                                </div>
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={rev6.series} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                <XAxis dataKey="long" stroke="#666" fontSize={11} />
                                <YAxis stroke="#666" fontSize={11} tickFormatter={(v) => formatINR(v)} />
                                <Tooltip
                                    contentStyle={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 6 }}
                                    formatter={(v, name) => v == null ? ['—', name] : [formatINR(v), name === 'actual' ? 'Actual Won' : 'Projected']}
                                />
                                <ReferenceLine
                                    x={rev6.series.find(s => s.is_current)?.long}
                                    stroke="#016a6d"
                                    strokeDasharray="4 4"
                                    opacity={0.5}
                                    label={{ value: 'Today', fill: '#016a6d', fontSize: 10, position: 'top' }}
                                />
                                <Bar dataKey="actual" fill="#34C759" name="actual" barSize={40} radius={[4, 4, 0, 0]} />
                                <Line type="monotone" dataKey="projected" stroke="#016a6d" strokeWidth={2.5} strokeDasharray="5 3" dot={{ r: 4, fill: '#016a6d' }} name="projected" />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {/* Customer × Month projection */}
                {projection && (
                    <div className="surface-1 rounded-md p-4 md:p-6 print-block" data-testid="projection-widget">
                        {/* Print-only header: brand + title + date. Hidden on-screen via CSS. */}
                        <div className="print-header items-center justify-between pb-4 mb-4 border-b border-slate-300">
                            <img src={LOGO_URL} alt="Ikas Corp" className="h-10 w-auto" />
                            <div className="text-right">
                                <div className="font-display text-lg font-bold text-slate-900">4-Month Sales Projection</div>
                                <div className="text-xs text-slate-500">Generated {printedOn} · Ikas Corp Sales CRM</div>
                            </div>
                        </div>                        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <CalendarRange className="w-4 h-4 text-[#016a6d]" />
                                    <div className="overline">4-Month Projection</div>
                                </div>
                                <h3 className="font-display text-lg md:text-xl font-bold text-slate-900 mt-1">Customer × Month · Current + Next 3</h3>
                                <p className="text-[11px] text-slate-500 mt-1">Open deals grouped by the <span className="text-slate-900">Expected Month</span> set on each lead. Tap a cell to drill down.</p>
                            </div>
                            <div className="text-right">
                                <div className="overline">4-Month Total</div>
                                <div className="font-display text-xl md:text-2xl font-bold text-[#016a6d] tabular-nums mt-1" data-testid="projection-grand-total">{formatINR(projection.grand_total)}</div>
                                <div className="text-[10px] text-slate-500 tabular-nums mt-0.5">Weighted: {formatINR(projection.grand_weighted)}</div>
                            </div>
                        </div>

                        {projection.customers.length === 0 ? (
                            <div className="text-center text-sm text-slate-500 py-8">
                                No open deals with expected month in the next 4 months.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm min-w-[640px]" data-testid="projection-table">
                                    <thead className="bg-[#F1F5F9] border-y border-[#E2E8F0]">
                                        <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                            <th className="px-3 md:px-4 py-3">Customer</th>
                                            {projection.months.map((m, i) => (
                                                <th key={m.key} className="px-3 md:px-4 py-3 text-right whitespace-nowrap">
                                                    {m.long}
                                                    {i === 0 && <div className="text-[9px] text-[#016a6d] normal-case tracking-normal mt-0.5">This month</div>}
                                                </th>
                                            ))}
                                            <th className="px-3 md:px-4 py-3 text-right border-l border-[#E2E8F0] whitespace-nowrap">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {projection.customers.map((c, i) => (
                                            <tr key={c.customer_name} data-testid={`projection-row-${i}`} className="border-b border-[#E2E8F0] hover:bg-[#FFFFFF]">
                                                <td className="px-3 md:px-4 py-3 text-slate-900 truncate max-w-[200px] md:max-w-[240px]">{c.customer_name}</td>
                                                {projection.months.map(m => {
                                                    const cell = c.months[m.key];
                                                    const hasValue = cell.value > 0;
                                                    if (!hasValue) {
                                                        return <td key={m.key} className="px-3 md:px-4 py-3 text-right text-slate-300">—</td>;
                                                    }
                                                    return (
                                                        <td key={m.key} className="text-right tabular-nums p-0">
                                                            <button
                                                                type="button"
                                                                onClick={() => setDrilldown({ customer: c.customer_name, month: m, deals: cell.deals, cell })}
                                                                data-testid={`projection-cell-${i}-${m.key}`}
                                                                className="w-full h-full px-3 md:px-4 py-3 rounded text-right hover:bg-[#016a6d]/10 hover:text-[#016a6d] text-slate-900 transition-colors group whitespace-nowrap"
                                                                title={`View ${cell.count} deal${cell.count > 1 ? 's' : ''} for ${c.customer_name} in ${m.long}`}
                                                            >
                                                                <div className="flex items-center justify-end gap-1">
                                                                    {formatINR(cell.value)}
                                                                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                                </div>
                                                                <div className="text-[10px] text-slate-500">{cell.count} deal{cell.count > 1 ? 's' : ''}</div>
                                                            </button>
                                                        </td>
                                                    );
                                                })}
                                                <td className="px-3 md:px-4 py-3 text-right tabular-nums text-[#016a6d] font-medium border-l border-[#E2E8F0] whitespace-nowrap">
                                                    {formatINR(c.total)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-[#F1F5F9] border-t border-[#E2E8F0]">
                                        <tr>
                                            <td className="px-3 md:px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500">Month Total</td>
                                            {projection.months.map(m => {
                                                const ct = projection.col_totals[m.key];
                                                return (
                                                    <td key={m.key} className="px-3 md:px-4 py-3 text-right tabular-nums text-slate-900 whitespace-nowrap">
                                                        {formatINR(ct.value)}
                                                        <div className="text-[10px] text-slate-500">{ct.count} deal{ct.count !== 1 ? 's' : ''}</div>
                                                    </td>
                                                );
                                            })}
                                            <td className="px-3 md:px-4 py-3 text-right tabular-nums text-[#016a6d] font-bold border-l border-[#E2E8F0] whitespace-nowrap">{formatINR(projection.grand_total)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Area chart */}
                <div className="surface-1 rounded-md p-4 md:p-6 no-print" data-testid="area-widget">
                    <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                        <div>
                            <div className="overline">Orders by Area</div>
                            <h3 className="font-display text-lg md:text-xl font-bold text-slate-900 mt-1">Geographic Distribution</h3>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <F label="Win/Loss" val={areaFilter.win_loss} onChange={v => setAreaFilter({ ...areaFilter, win_loss: v })} options={params.win_loss} testId="area-winloss" />
                            <F label="Owner" val={areaFilter.sales_owner} onChange={v => setAreaFilter({ ...areaFilter, sales_owner: v })} options={params.sales_owners} testId="area-owner" />
                            <F label="Service" val={areaFilter.service_line} onChange={v => setAreaFilter({ ...areaFilter, service_line: v })} options={params.service_lines} testId="area-service" />
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={area.slice(0, 15)}>
                            <XAxis dataKey="area" stroke="#666" fontSize={10} angle={-30} textAnchor="end" height={80} />
                            <YAxis stroke="#666" fontSize={11} />
                            <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 6 }} />
                            <Bar dataKey="count" fill="#016a6d" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="surface-1 rounded-md p-4 md:p-6 no-print" data-testid="source-widget">
                        <div className="overline">Lead Source Mix</div>
                        <h3 className="font-display text-lg md:text-xl font-bold text-slate-900 mt-1 mb-4">Channel Performance</h3>
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie data={sources} dataKey="leads" nameKey="source" cx="50%" cy="50%" outerRadius={90} innerRadius={50}>
                                    {sources.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                </Pie>
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 6 }} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
                            {sources.map(s => (
                                <div key={s.source} className="text-xs flex items-center justify-between border-b border-[#E2E8F0] py-2">
                                    <span className="text-slate-500">{s.source}</span>
                                    <span className="text-slate-900 tabular-nums">{s.leads} · {formatINR(s.pipeline_value)}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="surface-1 rounded-md p-4 md:p-6 no-print" data-testid="month-widget">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                            <div>
                                <div className="overline">Month-wise Pipeline</div>
                                <h3 className="font-display text-lg md:text-xl font-bold text-slate-900 mt-1">Expected Close</h3>
                            </div>
                            <select value={selMonth} onChange={e => setSelMonth(e.target.value)} data-testid="month-select" className="h-9 bg-[#F1F5F9] border border-[#E2E8F0] rounded-md text-slate-900 text-sm px-3">
                                <option value="">All Months</option>
                                {(params.expected_months || []).map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        <div className="max-h-[340px] overflow-y-auto space-y-2">
                            {monthPipe.map(l => (
                                <div key={l.id} className="flex items-center justify-between text-sm py-2 border-b border-[#E2E8F0]">
                                    <div className="min-w-0">
                                        <div className="text-slate-900 truncate">{l.customer_name}</div>
                                        <div className="text-xs text-slate-500 truncate">{l.service_offered} · {l.sales_owner}</div>
                                    </div>
                                    <div className="text-right shrink-0 ml-3">
                                        <div className="text-slate-900 tabular-nums">{formatINR(l.proposal_value)}</div>
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">{l.expected_month}</div>
                                    </div>
                                </div>
                            ))}
                            {monthPipe.length === 0 && <div className="text-center text-slate-500 py-8 text-sm">No open deals</div>}
                        </div>
                    </div>
                </div>
            </div>

            {/* Drilldown dialog */}
            <Dialog open={!!drilldown} onOpenChange={(o) => !o && setDrilldown(null)}>
                <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-3xl w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto" data-testid="drilldown-dialog">
                    {drilldown && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display text-lg md:text-xl">
                                    {drilldown.customer} · {drilldown.month.long}
                                </DialogTitle>
                                <DialogDescription className="text-slate-500">
                                    {drilldown.cell.count} open deal{drilldown.cell.count > 1 ? 's' : ''} · Total {formatINR(drilldown.cell.value)} · Weighted {formatINR(drilldown.cell.weighted)}
                                </DialogDescription>
                            </DialogHeader>
                            <div className="overflow-x-auto -mx-4 md:-mx-6">
                                <table className="w-full text-sm min-w-[560px]">
                                    <thead className="bg-[#F1F5F9] border-y border-[#E2E8F0]">
                                        <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                            <th className="px-3 md:px-4 py-2">Lead ID</th>
                                            <th className="px-3 md:px-4 py-2">Service</th>
                                            <th className="px-3 md:px-4 py-2">Owner</th>
                                            <th className="px-3 md:px-4 py-2">Stage</th>
                                            <th className="px-3 md:px-4 py-2 text-right">Value</th>
                                            <th className="px-3 md:px-4 py-2 text-right">Prob</th>
                                            <th className="px-3 md:px-4 py-2">Expected Close</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {drilldown.deals.map(d => (
                                            <tr
                                                key={d.id}
                                                onClick={() => { setDrilldown(null); nav(`/leads/${d.id}`); }}
                                                data-testid={`drilldown-deal-${d.lead_id}`}
                                                className="border-b border-[#E2E8F0] hover:bg-[#F1F5F9] cursor-pointer transition-colors"
                                            >
                                                <td className="px-3 md:px-4 py-3 text-slate-500 tabular-nums text-xs">{d.lead_id}</td>
                                                <td className="px-3 md:px-4 py-3 text-slate-700 text-xs truncate max-w-[200px]">{d.service_offered || '—'}</td>
                                                <td className="px-3 md:px-4 py-3 text-slate-700 text-xs">{d.sales_owner || '—'}</td>
                                                <td className="px-3 md:px-4 py-3 text-xs text-slate-500">{d.stage}</td>
                                                <td className="px-3 md:px-4 py-3 text-right tabular-nums text-slate-900">{formatINR(d.proposal_value)}</td>
                                                <td className="px-3 md:px-4 py-3 text-right tabular-nums text-xs text-slate-500">{Math.round((d.probability || 0) * 100)}%</td>
                                                <td className="px-3 md:px-4 py-3 text-xs text-slate-500">{d.expected_close_date ? formatDate(d.expected_close_date) : (d.expected_month || '—')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p className="text-xs text-slate-500 pt-2">Tap a row to open the full lead detail.</p>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function F({ label, val, onChange, options, testId }) {
    return (
        <select value={val || ''} onChange={e => onChange(e.target.value)} data-testid={testId} className="h-9 bg-[#F1F5F9] border border-[#E2E8F0] rounded-md text-slate-900 text-xs px-2">
            <option value="">{label}: All</option>
            {(options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
    );
}
